import React, { useState, useRef, useEffect } from 'react';
import './MultiSelect.css';

function MultiSelect({ options, selected, onChange, placeholder = 'Sélectionner...', colorMap, searchable = false, className = '' }) {
  const [open, setOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const ref = useRef(null);
  const searchRef = useRef(null);

  useEffect(() => {
    const handleClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  useEffect(() => {
    if (open && searchable && searchRef.current) {
      setTimeout(() => searchRef.current?.focus(), 50);
    }
    if (!open) setSearchTerm('');
  }, [open, searchable]);

  const toggle = (value) => {
    const next = selected.includes(value)
      ? selected.filter(v => v !== value)
      : [...selected, value];
    onChange(next);
  };

  const removeChip = (e, value) => {
    e.stopPropagation();
    onChange(selected.filter(v => v !== value));
  };

  const getLabel = (value) => {
    const opt = options.find(o => o.value === value);
    return opt ? opt.label : value;
  };

  const getColor = (value) => {
    if (!colorMap) return null;
    return colorMap[value] || null;
  };

  const filteredOptions = searchable && searchTerm
    ? options.filter(opt => opt.label.toLowerCase().includes(searchTerm.toLowerCase()))
    : options;

  return (
    <div className={`multi-select ${className}`} ref={ref}>
      <div className={`multi-select-trigger${open ? ' open' : ''}`} onClick={() => setOpen(!open)}>
        {selected.length === 0 ? (
          <span className="multi-select-placeholder">{placeholder}</span>
        ) : (
          <div className="multi-select-chips">
            {selected.map(val => {
              const color = getColor(val);
              return (
                <span
                  key={val}
                  className="multi-select-chip"
                  style={color ? { background: color + '18', color: color, borderColor: color + '40' } : {}}
                >
                  {getLabel(val)}
                  <button type="button" className="chip-remove" onClick={(e) => removeChip(e, val)}>&times;</button>
                </span>
              );
            })}
          </div>
        )}
        <svg className="multi-select-arrow" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </div>
      {open && (
        <div className="multi-select-dropdown">
          {searchable && (
            <div className="multi-select-search">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="11" cy="11" r="8" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              <input
                ref={searchRef}
                type="text"
                placeholder="Rechercher..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          )}
          {filteredOptions.map(opt => {
            const isSelected = selected.includes(opt.value);
            const color = getColor(opt.value);
            return (
              <div
                key={opt.value}
                className={`multi-select-option ${isSelected ? 'selected' : ''}`}
                onClick={() => toggle(opt.value)}
              >
                <span
                  className="option-check"
                  style={isSelected && color ? { background: color, borderColor: color } : {}}
                >
                  {isSelected && (
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                  )}
                </span>
                <span className="option-label" style={color ? { color } : {}}>
                  {opt.label}
                </span>
              </div>
            );
          })}
          {searchable && filteredOptions.length === 0 && (
            <div className="multi-select-no-results">Aucun résultat</div>
          )}
        </div>
      )}
    </div>
  );
}

export default MultiSelect;
